<template>
    <div>
        <v-container fluid class="pt-1">

            <v-row dense>

                <v-col cols="4" offset="8">

                    <v-text-field
                            class="mr-2"
                            v-model="search"
                            prepend-icon="mdi-magnify"
                            label="Search"
                            single-line
                            hide-details
                            clearable
                            solo
                            background-color="grey darken-2"
                            dense
                    ></v-text-field>
                </v-col>
            </v-row>

        </v-container>

        <v-data-table
                :headers="headers"
                :items="definitions"
                :loading="loading"
                :footer-props="{itemsPerPageOptions:[10,25,50,-1]}"
                sort-by="updated_at"
                :sort-desc="true"
                :search="search"
        >
            <template v-slot:body="{ items }">
                <tbody>

                <!-- NO DATA INFO -->
                <tr v-if="items.length===0 && !loading">
                    <td colspan="6">
                        <v-alert
                                color="primary"
                                class="mt-4 body-2"
                                icon="mdi-information">
                            No segments found
                        </v-alert>
                    </td>
                </tr>

                <!-- ITEMS -->
                <tr v-for="item in items" :key="item.id">
                    <td>
                        <avatar :image-url="item.created_by.photo_url" :person-name="item.created_by.name">
                            {{item.created_by.name}}
                        </avatar>
                    </td>

                    <td class="cursor-pointer" @click="editButtonClicked(item)">
                        {{item.name}}
                    </td>

                    <!-- TYPE -->
                    <td>
                        <v-chip pill color="primary" text-color="white">
                            <v-icon small v-if="item.type==='filter'">mdi-filter</v-icon>
                            <v-icon small v-if="item.type==='query'">mdi-magnify</v-icon>
                            {{ucFirst(item.type)}}
                        </v-chip>
                    </td>

                    <!-- SHARE WITH TEAM -->
                    <td v-if="!selectOnly">

                        <v-switch
                                class="mt-0"
                                @click.capture.stop=""
                                hide-details
                                color="primary"
                                ripple
                                :disabled="selectOnly"
                                v-model="item.shared"
                                @change="shareToggleClicked(item)"
                        ></v-switch>

                    </td>

                    <!-- LAST MODIFIED -->
                    <td>
                        <v-tooltip top color="primary">
                            <template v-slot:activator="{ on }">
                                <div class="text-no-wrap" v-on="on">
                                    {{ toHumanTill(item.updated_at) }}
                                </div>
                            </template>
                            <div class="body-2 ma-2">
                                <v-icon class="mr-2">mdi-clock</v-icon>
                                {{ toLocalDateTime(item.updated_at) }}
                            </div>
                        </v-tooltip>
                    </td>

                    <!-- ACTIONS -->
                    <td v-if="!selectOnly">

                        <div style="white-space: nowrap;">

                            <v-tooltip top color="primary" v-if="hasPermission('create queries')">
                                <template v-slot:activator="{ on }">
                                    <v-btn
                                            class="ma-0"
                                            icon
                                            text
                                            v-on="on"
                                            color="secondary"
                                            @click.stop="cloneButtonClicked(item)"
                                    >
                                        <v-icon light small color="white">mdi-file-multiple</v-icon>
                                    </v-btn>
                                </template>
                                <span>Clone</span>
                            </v-tooltip>

                            <v-tooltip color="primary" top v-if="hasPermission('delete queries')">
                                <template v-slot:activator="{ on }">
                                    <v-btn class="ma-0"
                                           icon
                                           text
                                           v-on="on"
                                           color="secondary"
                                           @click.stop="deleteButtonClicked(item.id)"
                                    >
                                        <v-icon light small color="white">mdi-delete</v-icon>
                                    </v-btn>
                                </template>
                                <span>Delete</span>
                            </v-tooltip>
                        </div>
                    </td>
                </tr>
                </tbody>
            </template>

        </v-data-table>

        <confirmation-dialog
                v-model="confirmationDialogModel"
                headline="Confirm delete"
                :delete="true"
                description="Are you sure you want to delete this segment?"
                @selected="deleteConfirmed"
        ></confirmation-dialog>
    </div>
</template>

<script>

    import {mapState, mapMutations, mapGetters, mapActions} from 'vuex';
    import {timeDisplay} from '../../../components/mixins/timeDisplayMixin';
    import Avatar from '../../../components/UI/Avatar';
    import ConfirmationDialog from '../../../components/UI/ConfirmationDialog';

    export default {

        components: {
            Avatar,
            ConfirmationDialog
        },

        mixins: [timeDisplay],

        props: {
            selectOnly: {
                required: false,
                type: Boolean,
                default: false,
            },
            filtersOnly: {
                required: false,
                type: Boolean,
                default: false,
            }
        },

        data() {
            return {
                confirmationDialogModel: false,
                deleteSegmentId: null,
                search: null,

                headersBase: [
                    {
                        text: 'OWNER',
                        value: 'created_by.name',
                        sortable: false,
                        width: '70px',
                    },
                    {
                        text: 'NAME',
                        align: 'left',
                        value: 'name',
                    },
                    {
                        text: 'TYPE',
                        align: 'left',
                        value: 'type',
                    },
                ],

                headersExtended: [
                    {
                        text: 'SHARE WITH TEAM',
                        align: 'left',
                        value: 'shared',
                    },
                ]
            }
        },
        computed: {

            ...mapState('Segments', [
                'segments',
                'segmentsLoading'
            ]),

            headers() {
                let headers = [...this.headersBase];

                if (!this.selectOnly) {
                    headers = [...headers, ...this.headersExtended];
                }

                headers.push({
                    text: 'LAST MODIFIED',
                    align: 'left',
                    value: 'updated_at',
                });

                if (!this.selectOnly) {
                    headers.push({
                        text: 'ACTIONS',
                        sortable: false,
                        align: 'left',
                        width: '120px'
                    });
                }

                return headers;
            },

            definitions() {
                return this.segments.filter((def) => {
                    if (def.created_by.id === this.$store.state.user.id) {
                        if (this.filtersOnly) {
                            return def.type === 'filter';
                        }
                        return true;
                    }
                    return false;
                });
            },

            loading() {
                return this.segmentsLoading;
            }
        },

        methods: {

            ...mapActions('Segments', [
                'deleteSegment',
                'cloneSegment',
                'shareSegment'
            ]),
            deleteButtonClicked(id) {
                this.deleteSegmentId = id;
                this.confirmationDialogModel = true;
            },

            deleteConfirmed(selected) {
                if (selected) {
                    this.deleteSegment(this.deleteSegmentId)
                        .then(() => {
                            Bus.$emit('flash-message', {message: 'Segment deleted...'});
                        })
                        .catch(({response}) => {
                            Bus.$emit('server-error');
                        });
                }
            },

            editButtonClicked(segment) {
                if (this.selectOnly) {
                    this.$emit("select", segment);
                } else {
                    this.$emit("edit", segment);
                }
            },

            cloneButtonClicked(segment) {
                this.cloneSegment(segment);
            },

            shareToggleClicked(segment) {
                this.shareSegment(segment)
                    .then(() => {
                    })
                    .catch(({response}) => {
                        Bus.$emit('server-error');
                    });
            },
        },

        created() {
            if (!this.$store.state.Segments) {
                this.$store.registerModule('Segments', Store);
            }
        },
    }
</script>
